import React, { useState } from 'react';
import { Button, CircularProgress, Box, Typography } from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BusinessIcon from '@mui/icons-material/Business';
import GroupsIcon from '@mui/icons-material/Groups';
import { signInWithPopup } from 'firebase/auth';
import { auth, googleProvider } from '../utils/firebaseClient';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { sendEmailVerification } from '../utils/firebaseClient';

const GoogleSignInButton = ({
  role,
  onSignInError,
  planPrice,
  trialDays,
  isPricingLoading,
  hideBanner = false,
}) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleGoogleSignIn = async () => {
    if (!role) {
      onSignInError?.('Please select a role first');
      return;
    }
    setLoading(true);
    try {
      // Firebase popup — no redirect, no Supabase OAuth
      const result = await signInWithPopup(auth, googleProvider);
      const firebaseUser = result.user;
      const idToken = await firebaseUser.getIdToken();

      // Build session object matching what googleSyncHandler expects
      const session = {
        user: {
          id: firebaseUser.uid,
          email: firebaseUser.email,
          user_metadata: {
            full_name: firebaseUser.displayName || '',
          },
        },
      };

      const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

      // Call your UNCHANGED backend google-sync endpoint
      const res = await axios.post(`${API_BASE}/api/auth/google-sync`, {
        session,
        role,
        forceLogin: false,
      });

      // In GoogleSignInButton.jsx, update the "existing user" block:

if (res.data.isNewUser) {
  // Store Firebase token in sessionStorage for SetupProfilePage to use
  try {
    await sendEmailVerification(result.user);
    console.log('Verification email sent via Firebase');
  } catch (verifyErr) {
    // Non-fatal — user can proceed, verify later
    console.warn('Could not send verification email:', verifyErr.message);
  }

  sessionStorage.setItem('firebaseToken', idToken);
  navigate(`/setup-profile?role=${role}`, {
    state: {
      prefill: {
        email: res.data.email,
        company_name: res.data.fullName || '',
      },
    },
  });
} else {
  // Existing user login
  const { user, token } = res.data;
  localStorage.setItem('jwtToken', token);
  localStorage.setItem('user', JSON.stringify(user));
  localStorage.setItem('role', user.role || role);
  sessionStorage.setItem('session_active', 'true');

  const validStatuses = ['on_trial', 'trialing', 'active', 'paid'];
  const userRole = user.role || role;

  if (userRole === 'carrier' && !validStatuses.includes(user.subscription_status)) {
    navigate('/payment', { replace: true });
  } else {
    // Navigate to their dashboard directly
    navigate(`/dashboard/${userRole}`, { replace: true });
  }
}
    } catch (err) {
      console.error('Google sign-in error:', err);
      // Firebase popup cancelled by user — don't show error
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
        setLoading(false);
        return;
      }
      onSignInError?.(err.response?.data?.message || err.message || 'Failed to sign in with Google');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Button
        onClick={handleGoogleSignIn}
        disabled={loading}
        fullWidth
        variant="outlined"
        size="large"
        sx={{
          py: 1.5, fontSize: '1rem', fontWeight: 600,
          bgcolor: '#ffffff', color: '#1f2937',
          border: '1px solid #e5e7eb', textTransform: 'none', boxShadow: 'none',
          '&:hover': { bgcolor: '#f9fafb', border: '1px solid #d1d5db', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
          '&:disabled': { bgcolor: '#f3f4f6', color: '#9ca3af', border: '1px solid #e5e7eb' },
        }}
      >
        {loading ? (
          <CircularProgress size={22} sx={{ color: '#6b7280' }} />
        ) : (
          <>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
              xmlns="http://www.w3.org/2000/svg" style={{ marginRight: 8, flexShrink: 0 }}>
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            Continue with Google
          </>
        )}
      </Button>

      {/* Pricing banner — unchanged */}
      {role && !hideBanner && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 },
          border: '0.5px solid', borderColor: 'divider', borderRadius: 2,
          px: 1.5, py: 1, backgroundColor: '#f8fafd', mt: 1,
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
        }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '50%',
            backgroundColor: role === 'shipper' ? '#e3f2fd' : role === 'carrier' ? '#fff8e1' : '#f3e5f5',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            {role === 'shipper' && <BusinessIcon sx={{ fontSize: 18, color: '#1565c0' }} />}
            {role === 'carrier' && <LocalShippingIcon sx={{ fontSize: 18, color: '#f9a825' }} />}
            {role === 'broker' && <GroupsIcon sx={{ fontSize: 18, color: '#6a1b9a' }} />}
          </Box>
          <Box sx={{ flex: 1, minWidth: { xs: '150px', sm: 'auto' } }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
              Your plan after trial
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#11224d', lineHeight: 1.4, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              {isPricingLoading ? 'Updating trial period...' : role === 'carrier'
                ? `${trialDays || 30}-day free trial included`
                : <>{`Free access`}<br />No subscription required</>}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
              {role === 'carrier'
                ? (!isPricingLoading && planPrice !== null
                  ? `$${Number(planPrice).toFixed(2)}/month after trial · Carrier plan`
                  : isPricingLoading ? 'Fetching price details...' : 'Pricing currently unavailable')
                : `${role.charAt(0).toUpperCase() + role.slice(1)} plan · No billing required`}
            </Typography>
          </Box>
          <Box sx={{
            fontSize: '11px', fontWeight: 600, px: 1, py: 0.4, borderRadius: '99px',
            backgroundColor: role === 'carrier' ? '#e8f5e9' : '#e3f2fd',
            color: role === 'carrier' ? '#2e7d32' : '#1565c0', whiteSpace: 'nowrap',
          }}>
            {role === 'carrier' ? 'Free trial' : 'Free access'}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default GoogleSignInButton;